/**
 * Adding this to a home screen, from inside the app.
 *
 * WHAT IS POSSIBLE, WHICH IS HALF OF IT. Chrome and Edge fire
 * `beforeinstallprompt` and let a page hold on to it and show the real install
 * dialog from a button of its own. Safari does not: Apple provides no
 * programmatic install on iOS at all, and the only route is Share → Add to Home
 * Screen. No amount of code changes that, so the honest thing is to detect
 * which case we are in and either offer the button or show the four words that
 * describe the manual route.
 *
 * A prompt is single-use. Once `prompt()` has been called the browser discards
 * it, and it is only re-fired on a later visit — so the button has to disappear
 * after it is used rather than sit there doing nothing.
 */
import { useEffect, useState } from 'react';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Already opened from a home screen, on either platform. */
function isInstalled(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS predates the standard and reports it here instead.
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

/** Safari on an iPhone or iPad, where the manual route is the only route. */
function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS reports itself as a Mac; the touch points give it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // Chrome and Firefox on iOS are Safari underneath but cannot install at all,
  // so the instructions would be wrong for them.
  return iOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export type InstallState =
  | { kind: 'installed' }
  | { kind: 'prompt'; install: () => Promise<void> }
  | { kind: 'ios' }
  | { kind: 'none' };

export function useInstall(): InstallState {
  const [event, setEvent] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isInstalled);

  useEffect(() => {
    const onPrompt = (e: Event): void => {
      // Chrome shows its own bar unless this is called; we are replacing it
      // with a button somewhere it can be found on purpose.
      e.preventDefault();
      setEvent(e as InstallPromptEvent);
    };
    const onInstalled = (): void => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return { kind: 'installed' };
  if (event) {
    return {
      kind: 'prompt',
      install: async () => {
        await event.prompt();
        await event.userChoice;
        // Spent either way: accepted installs, dismissed cannot be re-shown.
        setEvent(null);
      },
    };
  }
  if (isIosSafari()) return { kind: 'ios' };
  // A desktop browser, or one that has already had its prompt this session.
  return { kind: 'none' };
}
